import { useRef, useEffect, useMemo, useState } from "react";
import { Bot, User, Send, Loader2, MessageSquare, Clock } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  formatQuotaBannerCountdown,
  formatQuotaResetDateFrUtc,
  sourceRelevanceDisplayPercents,
} from "../../utils/format";
import { brandStyle, focusRingStyle, safeHex, brandSecondaryStyle } from "../../utils/branding";

const EMPTY_QUOTAS = {};

const assistantMarkdownComponents = {
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-blue-600 underline decoration-blue-200 underline-offset-2 hover:decoration-blue-500"
    >
      {children}
    </a>
  ),
  table: ({ children }) => (
    <div className="my-4 w-full overflow-x-auto rounded-lg border border-stone-200 bg-white shadow-sm">
      <table className="min-w-full border-collapse text-left text-[15px] leading-7">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="border-b border-stone-200 bg-stone-50">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="min-w-[140px] px-5 py-3 font-semibold text-stone-800 whitespace-nowrap">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border-t border-stone-100 px-5 py-3 text-stone-700 align-top">
      {children}
    </td>
  ),
  code: ({ className, children, ...props }) => {
    const inline = !className;
    if (inline) {
      return (
        <code
          className="rounded bg-stone-100 px-1.5 py-0.5 font-mono text-[0.875em] text-stone-800"
          {...props}
        >
          {children}
        </code>
      );
    }
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="my-3 overflow-x-auto rounded-xl border border-stone-800 bg-stone-900 p-4 text-sm text-stone-100 shadow-inner [&>code]:bg-transparent [&>code]:p-0 [&>code]:text-inherit">
      {children}
    </pre>
  ),
};

const STREAM_PHASE_LABELS = {
  preparing: "Préparation de la question…",
  searching: "Recherche dans les documents…",
  generating: "Génération de la réponse…",
};

export default function ChatWindow({
  config,
  messages,
  input,
  setInput,
  loading,
  streamPhase = null,
  onSend,
  quotas = EMPTY_QUOTAS,
}) {
  const bottomRef = useRef(null);
  const mdComponents = useMemo(() => assistantMarkdownComponents, []);
  const msgQuota = quotas.messages;
  /** Quota personnel : seulement si le client a un plafond mensuel messages (sinon illimité). */
  const accessMsg = msgQuota ? quotas.access_messages : null;
  const [nowTick, setNowTick] = useState(() => Date.now());
  const loadingCaption =
    (streamPhase && STREAM_PHASE_LABELS[streamPhase]) || "Analyse en cours…";
  const lastMessage =
    messages.length > 0 ? messages[messages.length - 1] : null;
  const hasInlineAssistantLoading =
    !!lastMessage &&
    lastMessage.role === "assistant" &&
    !!lastMessage.isStreaming &&
    !lastMessage.isError;

  useEffect(() => {
    // Scroll to bottom whenever messages or loading state changes
    // Using requestAnimationFrame to ensure DOM is updated before scroll
    const frame = requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    });
    return () => cancelAnimationFrame(frame);
  }, [messages, loading]);

  useEffect(() => {
    const iso = msgQuota?.resets_at || accessMsg?.resets_at;
    if (!iso) return undefined;
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, [msgQuota?.resets_at, accessMsg?.resets_at]);

  return (
    <>
      <main className="flex-1 overflow-y-auto px-4 py-6 sm:p-6 scroll-smooth bg-stone-50/30">
        <div className="max-w-4xl mx-auto space-y-6 pb-4">
          {msgQuota ? (
            <div
              className={`rounded-2xl border px-4 py-3 sm:px-5 sm:py-4 shadow-sm ${
                msgQuota.used >= msgQuota.max
                  ? "border-amber-200 bg-amber-50/90"
                  : msgQuota.used / msgQuota.max >= 0.9
                    ? "border-amber-100 bg-amber-50/50"
                    : "border-stone-200 bg-white"
              }`}
            >
              <div className="flex flex-wrap items-center gap-2 text-sm text-stone-800">
                <MessageSquare
                  size={18}
                  className="shrink-0 text-blue-600"
                  aria-hidden
                />
                <span className="font-semibold">
                  Quota messages (questions)
                </span>
                <span className="text-stone-600 tabular-nums">
                  {msgQuota.used} / {msgQuota.max} ce mois-ci (UTC)
                </span>
              </div>
              <p className="text-xs text-stone-600 mt-1">
                Total mensuel entreprise (questions), paramétré par
                l&apos;administrateur.
              </p>
              <div className="mt-2.5 h-2 rounded-full bg-stone-200 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min(100, (100 * msgQuota.used) / msgQuota.max)}%`,
                    backgroundColor: safeHex(config.primary_color),
                  }}
                />
              </div>
              {msgQuota.resets_at ? (
                <p className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-stone-600">
                  <Clock
                    size={14}
                    className="shrink-0 text-stone-500"
                    aria-hidden
                  />
                  <span>
                    Prochain renouvellement le{" "}
                    <strong className="font-medium text-stone-800">
                      {formatQuotaResetDateFrUtc(msgQuota.resets_at)}
                    </strong>{" "}
                    ({formatQuotaBannerCountdown(msgQuota.resets_at, nowTick)}).
                  </span>
                </p>
              ) : null}
            </div>
          ) : null}
          {accessMsg ? (
            <div
              className={`rounded-2xl border px-4 py-3 sm:px-5 sm:py-4 shadow-sm ${
                accessMsg.used >= accessMsg.max
                  ? "border-amber-200 bg-amber-50/90"
                  : accessMsg.used / accessMsg.max >= 0.9
                    ? "border-amber-100 bg-amber-50/50"
                    : "border-stone-200 bg-white"
              }`}
            >
              <div className="flex flex-wrap items-center gap-2 text-sm text-stone-800">
                <MessageSquare
                  size={18}
                  className="shrink-0 text-brand-red"
                  aria-hidden
                />
                <span className="font-semibold">
                  Votre quota personnel (questions)
                </span>
                <span className="text-stone-600 tabular-nums">
                  {accessMsg.used} / {accessMsg.max} ce mois-ci
                </span>
                {accessMsg.percent != null ? (
                  <span className="text-xs font-medium text-stone-500">
                    ({accessMsg.percent} % du total entreprise)
                  </span>
                ) : null}
              </div>
              {accessMsg.percent != null ? (
                <p className="text-xs text-stone-600 mt-1">
                  Plafond de votre accès :{" "}
                  <strong className="tabular-nums">{accessMsg.max}</strong>{" "}
                  message
                  {accessMsg.max > 1 ? "s" : ""} / mois (d&apos;après{" "}
                  {accessMsg.percent} % du total entreprise).
                </p>
              ) : null}
              <div className="mt-2.5 h-2 rounded-full bg-stone-200 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min(100, (100 * accessMsg.used) / accessMsg.max)}%`,
                    backgroundColor: safeHex(config.primary_color),
                  }}
                />
              </div>
              {accessMsg.resets_at ? (
                <p className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-stone-600">
                  <Clock
                    size={14}
                    className="shrink-0 text-stone-500"
                    aria-hidden
                  />
                  <span>
                    Renouvellement de votre part le{" "}
                    <strong className="font-medium text-stone-800">
                      {formatQuotaResetDateFrUtc(accessMsg.resets_at)}
                    </strong>{" "}
                    ({formatQuotaBannerCountdown(accessMsg.resets_at, nowTick)}
                    ).
                    {accessMsg.used >= accessMsg.max
                      ? " Quota personnel atteint jusqu'à cette date."
                      : ""}
                  </span>
                </p>
              ) : null}
            </div>
          ) : null}
          {messages.map((msg, idx) => {
            const isEmptyBot =
              msg.role === "assistant" &&
              !msg.content &&
              (!msg.sources || msg.sources.length === 0) &&
              !msg.isStreaming;
            if (isEmptyBot) return null;

            const sourcePct =
              msg.sources?.length > 0
                ? sourceRelevanceDisplayPercents(msg.sources)
                : null;

            return (
              <div
                key={msg.id ?? msg.localId ?? `${msg.role}-${idx}`}
                className={`flex gap-4 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"} animate-in fade-in slide-in-from-bottom-2 duration-300`}
              >
                <div
                  className={`size-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0 shadow-sm
                  ${msg.role === "user" ? "bg-white text-stone-600 border border-stone-200" : "text-white"}`}
                  style={msg.role === "assistant" ? brandStyle(config) : {}}
                >
                  {msg.role === "user" ? (
                    <User size={18} className="sm:w-5 sm:h-5" />
                  ) : (
                    <Bot size={18} className="sm:w-5 sm:h-5" />
                  )}
                </div>
                <div
                  className={`flex flex-col max-w-[85%] sm:max-w-[75%] ${msg.role === "user" ? "items-end" : "items-start"}`}
                >
                  <div
                    className={`px-5 py-3.5 rounded-2xl transition-all duration-200 ${msg.isError ? "bg-red-50 text-red-600 border border-red-100" : ""}
                    ${
                      msg.role === "user"
                        ? "rounded-tr-sm shadow-md"
                        : "bg-white text-stone-800 rounded-tl-sm shadow-sm border border-stone-100 prose prose-sm sm:prose-base prose-stone max-w-none prose-p:my-2 prose-p:leading-relaxed first:prose-p:mt-0 last:prose-p:mb-0 prose-headings:scroll-mt-4 prose-headings:font-semibold prose-h2:text-lg prose-h3:text-base prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-blockquote:border-l-blue-400 prose-blockquote:text-stone-600 prose-hr:border-stone-200"
                    }`}
                    style={msg.role === "user" ? brandSecondaryStyle(config) : {}}
                  >
                    {msg.role === "assistant" && !msg.isError ? (
                      msg.content?.trim() ? (
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={mdComponents}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      ) : (
                        <p className="text-stone-500 text-sm italic flex items-center gap-2 m-0">
                          <Loader2
                            className="animate-spin size-4 shrink-0 text-stone-400"
                            aria-hidden
                          />
                          {loadingCaption}
                        </p>
                      )
                    ) : (
                      <p className="whitespace-pre-wrap text-[15px]">
                        {msg.content}
                      </p>
                    )}
                  </div>

                  {msg.sources && msg.sources.length > 0 && (
                    <details className="mt-2 text-sm text-stone-500 bg-white border border-stone-200 rounded-xl overflow-hidden shadow-sm group w-full">
                      <summary className="px-4 py-2 cursor-pointer hover:bg-stone-50 transition-colors font-medium flex items-center gap-2 user-select-none list-none [&::-webkit-details-marker]:hidden">
                        <div className="size-4 rounded-full bg-stone-100 flex items-center justify-center group-open:rotate-90 transition-transform">
                          <svg
                            width="10"
                            height="10"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="m9 18 6-6-6-6" />
                          </svg>
                        </div>
                        Sources utilisées ({msg.sources.length})
                      </summary>
                      <div className="px-4 py-3 border-t border-stone-100 bg-stone-50/50">
                        <ul className="space-y-2">
                          {msg.sources.map((s, i) => (
                            <li key={typeof s === "string" ? s : s.source} className="flex flex-col gap-1">
                              <div className="flex items-center gap-2">
                                <span className="size-1.5 rounded-full bg-blue-400"></span>
                                <span className="font-semibold text-stone-700 truncate">
                                  {typeof s === "string" ? s : s.source}
                                </span>
                                {typeof s !== "string" &&
                                  sourcePct?.[i] != null && (
                                    <span
                                      className="text-xs px-1.5 py-0.5 rounded-md bg-blue-100 text-blue-700 font-mono"
                                      title="Pertinence relative à la question, parmi les extraits retenus pour cette réponse."
                                    >
                                      {sourcePct[i]}%
                                    </span>
                                  )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </details>
                  )}
                </div>
              </div>
            );
          })}

          {loading && !hasInlineAssistantLoading && (
            <div className="flex gap-4 flex-row animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div
                className="size-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0 shadow-sm"
                style={brandStyle(config)}
              >
                <Bot size={18} className="sm:w-5 sm:h-5 animate-pulse" />
              </div>
              <div className="flex flex-col gap-2 max-w-[85%] sm:max-w-[75%]">
                <div className="bg-white/80 backdrop-blur-sm px-5 py-4 rounded-2xl rounded-tl-sm shadow-sm border border-stone-100/50 flex flex-col gap-3 min-w-[200px] animate-pulse">
                  <div className="flex items-center gap-2">
                    <div className="size-1.5 rounded-full bg-blue-500 animate-ping"></div>
                    <span className="text-[11px] font-bold text-stone-400 uppercase tracking-[0.15em] ml-1">
                      {loadingCaption}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="h-2.5 bg-stone-100 rounded-full w-full overflow-hidden relative">
                      <div
                        className="absolute inset-x-0 inset-y-0 transtone-x-[-100%] animate-[shimmer_2s_infinite]"
                        style={{
                          background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)`,
                        }}
                      ></div>
                    </div>
                    <div className="h-2.5 bg-stone-100 rounded-full w-4/5 overflow-hidden relative">
                      <div
                        className="absolute inset-x-0 inset-y-0 transtone-x-[-100%] animate-[shimmer_2s_infinite_0.5s]"
                        style={{
                          background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={bottomRef} className="h-4" />
        </div>
      </main>

      <footer className="p-4 sm:p-6 bg-white border-t border-stone-200 shrink-0">
        <form
          onSubmit={onSend}
          className="max-w-4xl mx-auto relative flex items-end gap-2 bg-stone-50 rounded-2xl border border-stone-200 shadow-sm focus-within:ring-2 focus-within:border-transparent transition-all p-2"
          style={focusRingStyle(config)}
        >
          <textarea
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height =
                Math.min(e.target.scrollHeight, 200) + "px";
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSend(e);
              }
            }}
            placeholder={`Posez une question à ${config.name}…`}
            disabled={loading}
            className="w-full bg-transparent border-none focus:ring-0 resize-none p-3 text-stone-800 placeholder-stone-400 min-h-[52px] max-h-[200px]"
            rows={1}
          />
          <button
            type="submit"
            disabled={!input.trim() || loading}
            className="p-3.5 rounded-xl shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed shrink-0 mb-0.5 mr-0.5 hover:brightness-95"
            style={brandStyle(config)}
          >
            {loading ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <Send size={20} />
            )}
          </button>
        </form>
        <div className="text-center mt-3 text-xs text-stone-400">
          L'IA peut faire des erreurs. Vérifiez toujours les informations
          importantes.
        </div>
      </footer>
    </>
  );
}
